/* ----------------------------------------------------------------------------
 * SEO-B (W2a) data fetchers — Keyword Research, SERP analysis, Competitor
 * analysis, Domain comparison / content gaps.
 *
 * Self-contained and OWNED by Agent SEO-B-FE (kept out of the shared
 * data/sources.ts, which is orchestrator-wired). Mirrors the W1a domain-intel
 * fetchers exactly: gated behind USE_MOCK_DATA, honest nulls via numOrNull /
 * strOrNull, and every one of these four reads is PAID (DataForSEO, 7-day
 * cache) so it is USER-TRIGGERED ONLY — never fired on mount. Callers branch on
 * ApiError: 503 provider_unavailable (nothing fetched/charged), 402
 * budget_exceeded (refused before any provider call), 429 rate_limited, 502
 * provider_error (nothing stored). Shapes match the LOCKED API contract.
 * -------------------------------------------------------------------------- */

import { api, USE_MOCK_DATA } from '@/lib/api';
import type {
  CompetitorAnalysisResult,
  DomainComparisonResult,
  KeywordResearchResult,
  SerpAnalysisResult,
} from '@/types/seob';

/** Finite number or null — blank/absent/non-numeric all stay null. NEVER 0. */
function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Non-empty string or null — never coerces to a fabricated value. */
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

/* --- 1. Keyword research ----------------------------------------------------- */
// GET /seo/projects/{id}/keyword-research?seed=<kw>&limit=50 — PAID, 7d cache.

interface KeywordIdeaDto {
  keyword: string;
  volume?: number | string | null;
  cpc?: number | string | null;
  competition?: number | string | null;
  difficulty?: number | string | null;
}

const MOCK_KEYWORD_RESEARCH: Record<string, KeywordResearchResult> = {
  seo1: {
    keywords: [
      { keyword: 'community growth platform', volume: 1300, cpc: 3.4, competition: 0.42, difficulty: 38 },
      { keyword: 'online community for coaches', volume: 880, cpc: 5.1, competition: 0.55, difficulty: 44 },
      // null-honest row: provider returned no cpc / difficulty for this idea.
      { keyword: 'peer support group software', volume: null, cpc: null, competition: 0.28, difficulty: null },
    ],
    source: 'dataforseo.labs.keyword_ideas',
    fetched_at: '2026-07-16T06:20:00Z',
    cache_hit: false,
  },
  seo2: {
    keywords: [
      { keyword: 'booking software for therapists', volume: 720, cpc: 6.2, competition: 0.61, difficulty: 41 },
      { keyword: 'online booking system uk', volume: 2400, cpc: 4.8, competition: 0.7, difficulty: 55 },
    ],
    source: 'dataforseo.labs.keyword_ideas',
    fetched_at: '2026-07-15T18:12:00Z',
    cache_hit: true,
  },
};

/** Keyword ideas for a seed term (PAID, 7d cache). User-triggered only. */
export async function fetchSeoKeywordResearch(
  projectId: string,
  seed: string,
  limit = 50,
): Promise<KeywordResearchResult> {
  if (USE_MOCK_DATA) {
    const mock = MOCK_KEYWORD_RESEARCH[projectId] ?? {
      keywords: [],
      source: 'dataforseo.labs.keyword_ideas',
      fetched_at: null,
      cache_hit: false,
    };
    return { ...mock, keywords: mock.keywords.slice(0, limit) };
  }
  const res = await api.get<{
    keywords?: KeywordIdeaDto[];
    source?: string;
    fetched_at?: string | null;
    cache_hit?: boolean;
  }>(`/seo/projects/${encodeURIComponent(projectId)}/keyword-research`, { seed, limit });
  return {
    keywords: (res.keywords ?? []).map((k) => ({
      keyword: k.keyword,
      volume: numOrNull(k.volume),
      cpc: numOrNull(k.cpc),
      competition: numOrNull(k.competition),
      difficulty: numOrNull(k.difficulty),
    })),
    source: res.source ?? 'dataforseo.labs.keyword_ideas',
    fetched_at: res.fetched_at ?? null,
    cache_hit: res.cache_hit === true,
  };
}

/* --- 2. SERP analysis -------------------------------------------------------- */
// GET /seo/projects/{id}/serp-analysis?keyword=<kw>&location=<key> — PAID, 7d.

interface SerpRowDto {
  position?: number | string | null;
  domain?: string | null;
  url?: string | null;
  title?: string | null;
  type?: string | null;
}

const MOCK_SERP_ANALYSIS: Record<string, SerpAnalysisResult> = {
  seo1: {
    serp: [
      { position: 1, domain: 'meetup.com', url: 'https://www.meetup.com/', title: 'Meetup — find community groups', type: 'organic' },
      { position: 2, domain: 'circle.so', url: 'https://circle.so/', title: 'Circle — the all-in-one community platform', type: 'organic' },
      // null-honest: featured-snippet style row with no reported title.
      { position: 3, domain: 'grow2gether.com', url: 'https://grow2gether.com/', title: null, type: 'featured_snippet' },
    ],
    features: ['people_also_ask', 'related_searches'],
    source: 'dataforseo.serp.organic_live',
    fetched_at: '2026-07-16T06:24:00Z',
    cache_hit: false,
  },
  seo2: {
    serp: [
      { position: 1, domain: 'calendly.com', url: 'https://calendly.com/', title: 'Calendly — scheduling automation', type: 'organic' },
      { position: 2, domain: 'booksesh.com', url: 'https://booksesh.com/', title: 'BookSesh — booking for therapists', type: 'organic' },
    ],
    features: ['related_searches'],
    source: 'dataforseo.serp.organic_live',
    fetched_at: '2026-07-15T18:16:00Z',
    cache_hit: true,
  },
};

/** Live SERP for a keyword + location (PAID, 7d cache). User-triggered only. */
export async function fetchSeoSerpAnalysis(
  projectId: string,
  keyword: string,
  location: string,
): Promise<SerpAnalysisResult> {
  if (USE_MOCK_DATA) {
    return (
      MOCK_SERP_ANALYSIS[projectId] ?? {
        serp: [],
        features: [],
        source: 'dataforseo.serp.organic_live',
        fetched_at: null,
        cache_hit: false,
      }
    );
  }
  const res = await api.get<{
    serp?: SerpRowDto[];
    features?: (string | null)[];
    source?: string;
    fetched_at?: string | null;
    cache_hit?: boolean;
  }>(`/seo/projects/${encodeURIComponent(projectId)}/serp-analysis`, { keyword, location });
  return {
    serp: (res.serp ?? []).map((r) => ({
      position: numOrNull(r.position),
      domain: strOrNull(r.domain) ?? '—',
      url: strOrNull(r.url),
      title: strOrNull(r.title),
      type: strOrNull(r.type),
    })),
    features: (res.features ?? []).filter((f): f is string => typeof f === 'string' && f !== ''),
    source: res.source ?? 'dataforseo.serp.organic_live',
    fetched_at: res.fetched_at ?? null,
    cache_hit: res.cache_hit === true,
  };
}

/* --- 3. Competitor analysis -------------------------------------------------- */
// GET /seo/projects/{id}/competitor-analysis?limit=20 — PAID, 7d cache.

interface CompetitorRowDto {
  domain?: string | null;
  common_keywords?: number | string | null;
  overlap?: number | string | null;
  est_traffic?: number | string | null;
}

const MOCK_COMPETITOR_ANALYSIS: Record<string, CompetitorAnalysisResult> = {
  seo1: {
    competitors: [
      { domain: 'circle.so', common_keywords: 412, overlap: 0.34, est_traffic: 210000 },
      { domain: 'mightynetworks.com', common_keywords: 288, overlap: 0.27, est_traffic: 164000 },
      // null-honest: overlap / traffic not reported for this competitor.
      { domain: 'tribe.so', common_keywords: 96, overlap: null, est_traffic: null },
    ],
    source: 'dataforseo.labs.competitors_domain',
    fetched_at: '2026-07-16T06:26:00Z',
    cache_hit: false,
  },
  seo2: {
    competitors: [
      { domain: 'calendly.com', common_keywords: 530, overlap: 0.41, est_traffic: 980000 },
      { domain: 'acuityscheduling.com', common_keywords: 274, overlap: 0.22, est_traffic: 320000 },
    ],
    source: 'dataforseo.labs.competitors_domain',
    fetched_at: '2026-07-15T18:18:00Z',
    cache_hit: true,
  },
};

/** Competing domains for the project's domain (PAID, 7d). User-triggered only. */
export async function fetchSeoCompetitorAnalysis(
  projectId: string,
  limit = 20,
): Promise<CompetitorAnalysisResult> {
  if (USE_MOCK_DATA) {
    const mock = MOCK_COMPETITOR_ANALYSIS[projectId] ?? {
      competitors: [],
      source: 'dataforseo.labs.competitors_domain',
      fetched_at: null,
      cache_hit: false,
    };
    return { ...mock, competitors: mock.competitors.slice(0, limit) };
  }
  const res = await api.get<{
    competitors?: CompetitorRowDto[];
    source?: string;
    fetched_at?: string | null;
    cache_hit?: boolean;
  }>(`/seo/projects/${encodeURIComponent(projectId)}/competitor-analysis`, { limit });
  return {
    competitors: (res.competitors ?? []).map((c) => ({
      domain: strOrNull(c.domain) ?? '—',
      common_keywords: numOrNull(c.common_keywords),
      overlap: numOrNull(c.overlap),
      est_traffic: numOrNull(c.est_traffic),
    })),
    source: res.source ?? 'dataforseo.labs.competitors_domain',
    fetched_at: res.fetched_at ?? null,
    cache_hit: res.cache_hit === true,
  };
}

/* --- 4. Domain comparison / content gaps ------------------------------------ */
// GET /seo/projects/{id}/domain-comparison?competitor=<domain> — PAID, 7d.

interface DomainGapRowDto {
  keyword: string;
  your_position?: number | string | null;
  their_position?: number | string | null;
  volume?: number | string | null;
  opportunity?: string | null;
}

const MOCK_DOMAIN_COMPARISON: Record<string, DomainComparisonResult> = {
  seo1: {
    gaps: [
      { keyword: 'community engagement tools', your_position: null, their_position: 3, volume: 1600, opportunity: 'high' },
      { keyword: 'membership community software', your_position: 34, their_position: 6, volume: 720, opportunity: 'medium' },
      // null-honest: volume not reported for this gap keyword.
      { keyword: 'niche community builder', your_position: null, their_position: 12, volume: null, opportunity: 'low' },
    ],
    source: 'dataforseo.labs.domain_intersection',
    fetched_at: '2026-07-16T06:28:00Z',
    cache_hit: false,
  },
  seo2: {
    gaps: [
      { keyword: 'appointment reminder software', your_position: null, their_position: 4, volume: 2900, opportunity: 'high' },
      { keyword: 'group class booking', your_position: 19, their_position: 8, volume: 480, opportunity: 'medium' },
    ],
    source: 'dataforseo.labs.domain_intersection',
    fetched_at: '2026-07-15T18:20:00Z',
    cache_hit: true,
  },
};

/** Content-gap keywords vs one competitor domain (PAID, 7d). User-triggered. */
export async function fetchSeoDomainComparison(
  projectId: string,
  competitor: string,
): Promise<DomainComparisonResult> {
  if (USE_MOCK_DATA) {
    return (
      MOCK_DOMAIN_COMPARISON[projectId] ?? {
        gaps: [],
        source: 'dataforseo.labs.domain_intersection',
        fetched_at: null,
        cache_hit: false,
      }
    );
  }
  const res = await api.get<{
    gaps?: DomainGapRowDto[];
    source?: string;
    fetched_at?: string | null;
    cache_hit?: boolean;
  }>(`/seo/projects/${encodeURIComponent(projectId)}/domain-comparison`, { competitor });
  return {
    gaps: (res.gaps ?? []).map((g) => ({
      keyword: g.keyword,
      your_position: numOrNull(g.your_position),
      their_position: numOrNull(g.their_position),
      volume: numOrNull(g.volume),
      opportunity: strOrNull(g.opportunity) ?? '—',
    })),
    source: res.source ?? 'dataforseo.labs.domain_intersection',
    fetched_at: res.fetched_at ?? null,
    cache_hit: res.cache_hit === true,
  };
}
